import { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
    Autocomplete,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
} from "@mui/material";

import { externalApi } from "src/Api/externalApi";
import { cartApi } from "src/Api/cartApi";
import { ICountryData, IGuestNested } from "@interfaces";
import {
    emptyGuest,
    extractGuests,
    extractUserId,
    guestDisplayName,
    guestId,
    guestToDraft,
    GuestDraft,
} from "src/Utilities/addressBook";

/**
 * "Address Book" tab — lets a logged-in user view, add, edit and remove their
 * saved travellers (account-level "guests", stored in MongoDB).
 *
 *  - read   = `GET cart/customers` (returns the profile + `guests`)
 *  - add    = `POST cart/guests` without `_id`
 *  - edit   = `POST cart/guests` with `_id` (create-or-update upsert)
 *  - delete = `DELETE cart/guests/{guestId}` (`guestId` = `_id.$oid`)
 *
 * After every write we re-fetch so the list always reflects the server.
 */
export const ProfileAddressBookTab = () => {
    const [guests, setGuests] = useState<IGuestNested[]>([]);
    const [userId, setUserId] = useState<string | undefined>(undefined);
    const [countries, setCountries] = useState<ICountryData[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editId, setEditId] = useState<string | null>(null);
    const [draft, setDraft] = useState<GuestDraft>(emptyGuest());

    const loadGuests = useCallback(async () => {
        setLoading(true);
        try {
            const resp = await cartApi.getCustomers();
            setGuests(extractGuests(resp));
            setUserId(extractUserId(resp));
            setError(null);
        } catch {
            setError("We couldn't load your saved travellers. Please try again.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadGuests();
    }, [loadGuests]);

    useEffect(() => {
        externalApi
            .getCountriesDate()
            .then((res) => setCountries(res.data ?? []))
            .catch(() => {
                /* non-fatal — Country becomes a free-text fallback */
            });
    }, []);

    const isDraftValid = useMemo(
        () => draft.first_name.trim() !== "" && draft.last_name.trim() !== "",
        [draft],
    );

    const openAdd = () => {
        setDraft(emptyGuest());
        setEditId(null);
        setDialogOpen(true);
    };

    const openEdit = (g: IGuestNested) => {
        setDraft(guestToDraft(g));
        setEditId(guestId(g) ?? null);
        setDialogOpen(true);
    };

    const closeDialog = () => {
        if (saving) return;
        setDialogOpen(false);
    };

    const buildPayload = (): Partial<IGuestNested> => {
        const payload: Partial<IGuestNested> = {
            first_name: draft.first_name.trim(),
            last_name: draft.last_name.trim(),
            date_of_birth: draft.date_of_birth || null,
            email: draft.email || null,
            phone_number: draft.phone_number || null,
            post_code: draft.post_code || null,
            country_code: draft.country_code || null,
        };
        if (userId) payload.user_id = userId;
        if (editId) payload._id = { $oid: editId };
        return payload;
    };

    const handleSave = async () => {
        if (!isDraftValid) return;
        setSaving(true);
        try {
            await cartApi.saveGuest(buildPayload());
            await loadGuests();
            setDialogOpen(false);
        } catch {
            setError("We couldn't save your changes. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (g: IGuestNested) => {
        const id = guestId(g);
        if (!id) return;
        const name = guestDisplayName(g) || "this traveller";
        if (!window.confirm(`Remove ${name} from your address book?`)) return;
        setSaving(true);
        try {
            await cartApi.deleteGuest(id);
            await loadGuests();
        } catch {
            setError("We couldn't save your changes. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    const updateDraft = (patch: Partial<GuestDraft>) =>
        setDraft((prev) => ({ ...prev, ...patch }));

    return (
        <Wrapper>
            <Header>
                <div>
                    <h2>Address Book</h2>
                    <p className="subtitle">
                        Save travellers once and reuse them at checkout and in the Trip Planner.
                    </p>
                </div>
                <Button variant="contained" onClick={openAdd} disabled={loading}>
                    Add traveller
                </Button>
            </Header>

            {error && <ErrorBanner role="alert">{error}</ErrorBanner>}

            {loading ? (
                <EmptyState>Loading your saved travellers…</EmptyState>
            ) : guests.length === 0 ? (
                <EmptyState>
                    <strong>No saved travellers yet.</strong>
                    <span>Add a traveller to reuse their details later.</span>
                </EmptyState>
            ) : (
                <List>
                    {guests.map((g, index) => {
                        // Without a Mongo id we can't safely update or delete the
                        // record (an "edit" would create a duplicate), so disable
                        // those actions for the (unexpected) id-less row.
                        const id = guestId(g);
                        return (
                            <Card key={id ?? index}>
                                <div className="info">
                                    <span className="name">
                                        {guestDisplayName(g) || "Unnamed traveller"}
                                    </span>
                                    {g.email && <span className="meta">{g.email}</span>}
                                    {g.phone_number !== null && g.phone_number !== undefined && (
                                        <span className="meta">{g.phone_number}</span>
                                    )}
                                </div>
                                <div className="actions">
                                    <Button
                                        size="small"
                                        onClick={() => openEdit(g)}
                                        disabled={!id}
                                    >
                                        Edit
                                    </Button>
                                    <Button
                                        size="small"
                                        color="error"
                                        onClick={() => handleDelete(g)}
                                        disabled={!id}
                                    >
                                        Remove
                                    </Button>
                                </div>
                            </Card>
                        );
                    })}
                </List>
            )}

            <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
                <DialogTitle>{editId === null ? "Add traveller" : "Edit traveller"}</DialogTitle>
                <DialogContent>
                    <Form>
                        <Row>
                            <TextField
                                label="First name"
                                required
                                value={draft.first_name}
                                onChange={(e) => updateDraft({ first_name: e.target.value })}
                                fullWidth
                                size="small"
                            />
                            <TextField
                                label="Last name"
                                required
                                value={draft.last_name}
                                onChange={(e) => updateDraft({ last_name: e.target.value })}
                                fullWidth
                                size="small"
                            />
                        </Row>
                        <TextField
                            label="Date of birth"
                            type="date"
                            value={draft.date_of_birth}
                            onChange={(e) => updateDraft({ date_of_birth: e.target.value })}
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            label="Email"
                            type="email"
                            value={draft.email}
                            onChange={(e) => updateDraft({ email: e.target.value })}
                            fullWidth
                            size="small"
                        />
                        <TextField
                            label="Phone number"
                            value={draft.phone_number}
                            onChange={(e) => updateDraft({ phone_number: e.target.value })}
                            fullWidth
                            size="small"
                        />
                        <Row>
                            <TextField
                                label="Postcode"
                                value={draft.post_code}
                                onChange={(e) => updateDraft({ post_code: e.target.value })}
                                fullWidth
                                size="small"
                            />
                            {countries.length > 0 ? (
                                <Autocomplete
                                    fullWidth
                                    size="small"
                                    options={countries}
                                    autoHighlight
                                    getOptionLabel={(o) => o.name}
                                    isOptionEqualToValue={(o, v) =>
                                        o.countryCode === v.countryCode
                                    }
                                    value={
                                        countries.find(
                                            (c) => c.countryCode === draft.country_code,
                                        ) ?? null
                                    }
                                    onChange={(_e, val) =>
                                        updateDraft({ country_code: val?.countryCode ?? "" })
                                    }
                                    renderInput={(params) => (
                                        <TextField {...params} label="Country" size="small" />
                                    )}
                                />
                            ) : (
                                <TextField
                                    label="Country"
                                    value={draft.country_code}
                                    onChange={(e) =>
                                        updateDraft({ country_code: e.target.value })
                                    }
                                    fullWidth
                                    size="small"
                                />
                            )}
                        </Row>
                    </Form>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDialog} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={!isDraftValid || saving}
                    >
                        {saving ? "Saving…" : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Wrapper>
    );
};

const Wrapper = styled.div`
    background-color: #fff;
    border-radius: 4px;
    padding: 24px;

    @media (max-width: 600px) {
        padding: 16px;
    }
`;

const Header = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 20px;

    h2 {
        font-size: 22px;
        font-weight: 700;
        margin: 0;
    }
    .subtitle {
        margin: 4px 0 0;
        color: #6e7375;
        font-size: 13px;
    }
`;

const ErrorBanner = styled.div`
    background-color: #fdecea;
    color: #b3261e;
    border: 1px solid #f5c2bd;
    border-radius: 4px;
    padding: 12px 16px;
    margin-bottom: 16px;
    font-size: 14px;
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    align-items: center;
    text-align: center;
    color: #6e7375;
    padding: 48px 16px;
`;

const List = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const Card = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    border: 1px solid #e6e9eb;
    border-radius: 8px;
    padding: 14px 16px;

    .info {
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 0;
    }
    .name {
        font-weight: 600;
        font-size: 15px;
    }
    .meta {
        color: #6e7375;
        font-size: 13px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    .actions {
        display: flex;
        gap: 4px;
        flex-shrink: 0;
    }
`;

const Form = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding-top: 8px;
`;

const Row = styled.div`
    display: flex;
    gap: 16px;

    @media (max-width: 600px) {
        flex-direction: column;
    }
`;
